import { forwardRef, InputHTMLAttributes, useId } from 'react'

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
    /** `outlined`: a visible 1px border and no shadow, for fields on a white surface where the shadow-based primary field would not read as an input. */
    variant?: 'primary' | 'secondary' | 'outlined'
    inputType?: 'text' | 'number' | 'password'
    /** `true` shows the error ring only; a string additionally renders the message below and wires
     *  it up via `aria-describedby` (mirrors ds2/Select's `error`). */
    error?: boolean | string
    /** Accessible label rendered as a `<label htmlFor>` associated with the input.
     *  Divergence from @numeo-ai/components (which omits it, unlike its Select): without a real
     *  label consumers fall back to visually-labeled-but-unassociated sibling <span>s, so the
     *  field has no accessible name for screen readers. */
    label?: string
    required?: boolean
}

const hasErrorState = (error: boolean | string): boolean => !!error

export const Input = forwardRef<HTMLInputElement, InputProps>(
    ({ variant = 'primary', inputType = 'text', error = false, label, required = false, id, style, ...props }, ref) => {
        const isOutlined = variant === 'outlined'
        const fillClasses = variant === 'primary' ? 'bg-surface shadow-field' : isOutlined ? 'border border-border bg-surface' : 'bg-default'
        const stateClasses = isOutlined
            ? hasErrorState(error)
                ? 'border-danger ring-1 ring-danger'
                : 'focus-within:border-accent focus-within:ring-1 focus-within:ring-accent'
            : hasErrorState(error)
              ? 'ring-2 ring-danger'
              : 'focus-within:ring-2 focus-within:ring-accent'
        const generatedId = useId()
        const inputId = id ?? generatedId
        const hasError = !!error
        const errorMessage = typeof error === 'string' ? error : undefined
        const errorId = `${inputId}-error`

        const field = (
            <div
                data-field-box
                className={`relative flex h-9 items-center gap-1.5 rounded-xl px-3 transition-shadow duration-150 ${fillClasses} ${stateClasses}`}
                style={label || errorMessage ? undefined : style}
            >
                <input
                    ref={ref}
                    id={inputId}
                    type={inputType}
                    // required drives the `*` in the label AND the native constraint — destructuring it out
                    // without forwarding it here would silently drop HTML5 required-field validation.
                    required={required}
                    aria-invalid={hasError || undefined}
                    aria-describedby={errorMessage ? errorId : undefined}
                    className="min-w-0 flex-1 border-none bg-transparent p-0 text-sm font-normal leading-5 text-foreground outline-none"
                    {...props}
                />
            </div>
        )

        if (!label && !errorMessage) return field

        return (
            <div className="flex w-full flex-col gap-1.5" style={style}>
                {label && (
                    <label htmlFor={inputId} className="inline-flex flex-wrap items-center gap-1 whitespace-nowrap pr-2 text-sm font-medium text-foreground">
                        <span>{label}</span>
                        {required && <span className="text-danger">*</span>}
                    </label>
                )}
                {field}
                {errorMessage && (
                    <span id={errorId} className="text-xs text-danger">
                        {errorMessage}
                    </span>
                )}
            </div>
        )
    }
)
Input.displayName = 'Input'
